import { useEffect, useRef, useState } from "react";
import { Modal, ModalFooter, Button, Select, Label } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui";

const TARGET_FIELDS = [
  { key: "first_name",    label: "First name" },
  { key: "last_name",     label: "Last name" },
  { key: "email",         label: "Email" },
  { key: "phone",         label: "Phone" },
  { key: "address_line1", label: "Street" },
  { key: "city",          label: "City" },
  { key: "state",         label: "State" },
  { key: "postal_code",   label: "Postal code" },
  { key: "country",       label: "Country" },
  { key: "lifecycle_stage", label: "Lifecycle stage" },
  { key: "source",        label: "Source" },
  { key: "date_of_birth", label: "Date of birth" },
];

function parseCsv(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') inQuotes = false;
        else cur += ch;
      } else {
        if (ch === ",") { out.push(cur); cur = ""; }
        else if (ch === '"') inQuotes = true;
        else cur += ch;
      }
    }
    out.push(cur);
    return out;
  };

  const headers = parseLine(lines[0]!).map((h) => h.trim());
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseLine(lines[i]!);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => { row[h] = (cells[idx] ?? "").trim(); });
    rows.push(row);
  }
  return { headers, rows };
}

function autoMap(headers: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const h of headers) {
    const norm = h.toLowerCase().replace(/[^a-z0-9]/g, "");
    const match = TARGET_FIELDS.find((f) => f.key.replace(/_/g, "") === norm);
    if (match) map[h] = match.key;
    else if (norm.includes("first")) map[h] = "first_name";
    else if (norm.includes("last") || norm === "lastname" || norm === "surname") map[h] = "last_name";
    else if (norm.includes("phone") || norm.includes("mobile")) map[h] = "phone";
    else if (norm.includes("mail")) map[h] = "email";
    else if (norm === "name" || norm === "fullname") map[h] = "first_name"; // best-effort
    else map[h] = ""; // unmapped — drop
  }
  return map;
}

export function ContactImportDialog({ open, onClose, onImported }: {
  open: boolean;
  onClose: () => void;
  onImported: (summary: { created: number; updated: number; skipped: number }) => void;
}) {
  const toast = useToast();
  const [stage, setStage] = useState<"upload" | "map" | "progress">("upload");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [tag, setTag] = useState("");
  const [upsertBy, setUpsertBy] = useState<"email" | "phone" | "none">("email");
  // Her Boulevard client list is the import that matters: those people have
  // all been in before, so online booking must treat them as returning.
  const [returning, setReturning] = useState(true);
  const [importing, setImporting] = useState(false);
  // Async-path tracking. When the server returns 202 with an import_id,
  // we flip to the "progress" stage and poll /contacts/imports/:id every
  // 2 s until the job lands at completed/failed. The user can close the
  // dialog at any time — the worker keeps going either way; closing
  // just stops the visible progress bar.
  const [progress, setProgress] = useState<{
    importId: string;
    total: number;
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    status: "queued" | "running" | "completed" | "failed" | "cancelled";
    errors: { row: number; error: string }[];
  } | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const onFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? "");
      const { headers, rows } = parseCsv(text);
      if (headers.length === 0) {
        toast.error("Could not parse CSV — make sure the first row is column headers");
        return;
      }
      setHeaders(headers);
      setRows(rows);
      setMapping(autoMap(headers));
      setStage("map");
    };
    reader.readAsText(file);
  };

  const submit = async () => {
    setImporting(true);
    try {
      const body = {
        rows: rows.map((r) => {
          const out: Record<string, string | null> = {};
          for (const [k, v] of Object.entries(r)) out[k] = v === "" ? null : v;
          return out;
        }),
        mapping: Object.fromEntries(Object.entries(mapping).filter(([, v]) => v)),
        upsert_by: upsertBy,
        returning_clients: returning,
        ...(tag ? { add_tag: tag } : {}),
      };
      // Backend decides sync vs async based on row count. Small files
      // come back with `mode: "sync"` + final counters; large ones
      // return 202 with `import_id` and we switch to the polling stage.
      const res = await api.post<
        | { mode: "sync"; created: number; updated: number; skipped: number; errors: { row: number; error: string }[] }
        | { mode: "async" | "async-fallback-sync"; import_id: string; total: number }
      >("/contacts/import", body);

      if ("import_id" in res) {
        setProgress({
          importId: res.import_id,
          total: res.total,
          processed: 0,
          created: 0,
          updated: 0,
          skipped: 0,
          status: "queued",
          errors: [],
        });
        setStage("progress");
        toast.info(`Import queued — ${res.total.toLocaleString()} rows`);
      } else {
        toast.success(`Import complete: ${res.created} created, ${res.updated} updated, ${res.skipped} skipped`);
        onImported({ created: res.created, updated: res.updated, skipped: res.skipped });
        onClose();
        reset();
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // Polling loop for the async path. Mounts when progress is set and
  // we're sitting on the progress stage; stops on completion or
  // dialog close.
  useEffect(() => {
    if (stage !== "progress" || !progress) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get<{
          status: "queued" | "running" | "completed" | "failed" | "cancelled";
          total: number;
          processed: number;
          created: number;
          updated: number;
          skipped: number;
          errors: { row: number; error: string }[];
        }>(`/contacts/imports/${progress.importId}`);
        if (cancelled) return;
        setProgress((prev) => prev ? {
          ...prev,
          ...res,
          importId: prev.importId,
        } : prev);
        if (res.status === "completed" || res.status === "failed") {
          if (pollTimer.current) clearInterval(pollTimer.current);
          if (res.status === "completed") {
            toast.success(
              `Import complete: ${res.created} created, ${res.updated} updated, ${res.skipped} skipped${res.errors.length ? `, ${res.errors.length} errors` : ""}`
            );
            onImported({ created: res.created, updated: res.updated, skipped: res.skipped });
          } else {
            toast.error("Import failed — check the errors list");
          }
        }
      } catch { /* transient — keep polling */ }
    };
    void tick();
    pollTimer.current = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, progress?.importId]);

  const reset = () => {
    setStage("upload");
    setHeaders([]);
    setRows([]);
    setMapping({});
    setTag("");
    setReturning(true);
    setProgress(null);
    if (pollTimer.current) clearInterval(pollTimer.current);
  };

  return (
    <Modal open={open} onClose={() => { onClose(); reset(); }} title="Import contacts" size="lg">
      {stage === "progress" && progress ? (
        // Async path. Worker is chunking through rows; we poll
        // /contacts/imports/:id every 2 s and update this view. The
        // user can close the dialog at any time — the worker keeps
        // running and the import lands whether anyone's watching.
        <div className="space-y-4">
          <div>
            <p className="text-sm text-zinc-200">
              {progress.status === "completed"
                ? "Import complete"
                : progress.status === "failed"
                  ? "Import failed"
                  : `Importing ${progress.total.toLocaleString()} contacts…`}
            </p>
            <p className="mt-1 text-xs text-zinc-500">
              {progress.status === "running" || progress.status === "queued"
                ? "Safe to close this window — the import keeps running in the background."
                : null}
            </p>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-800">
            <div
              className={
                "h-full rounded-full transition-all duration-500 " +
                (progress.status === "failed"
                  ? "bg-rose-500"
                  : progress.status === "completed"
                    ? "bg-emerald-500"
                    : "bg-indigo-500")
              }
              style={{
                width: `${progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0}%`,
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <Stat label="Processed" value={`${progress.processed.toLocaleString()} / ${progress.total.toLocaleString()}`} />
            <Stat label="Created" value={progress.created.toLocaleString()} tone="emerald" />
            <Stat label="Updated" value={progress.updated.toLocaleString()} tone="indigo" />
            <Stat label="Skipped" value={progress.skipped.toLocaleString()} tone="zinc" />
          </div>
          {progress.errors.length > 0 && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
              <p className="text-xs font-medium text-rose-200">
                {progress.errors.length} row{progress.errors.length === 1 ? "" : "s"} failed
              </p>
              <ul className="mt-2 max-h-32 overflow-y-auto space-y-1 text-[11px] text-rose-300/90">
                {progress.errors.slice(0, 50).map((e, i) => (
                  <li key={i}>
                    <strong>Row {e.row}:</strong> {e.error}
                  </li>
                ))}
                {progress.errors.length > 50 && (
                  <li className="opacity-75">…and {progress.errors.length - 50} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      ) : stage === "upload" ? (
        <div>
          <label
            className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-zinc-700 bg-zinc-900/30 px-6 py-12 text-center hover:border-indigo-500/50 hover:bg-zinc-900/50 cursor-pointer transition-colors"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
          >
            <p className="text-sm font-medium text-zinc-200">Drop a CSV file here</p>
            <p className="text-xs text-zinc-500">or click to browse</p>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
            />
          </label>
          <p className="mt-3 text-xs text-zinc-500">
            First row should contain column headers. We&rsquo;ll let you map columns to contact fields next.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-zinc-500">Found <strong className="text-zinc-300">{rows.length}</strong> rows. Map your CSV columns to contact fields:</p>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-zinc-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 bg-zinc-900/50">
                  <th className="px-3 py-2 text-left font-medium text-zinc-400 text-xs">CSV column</th>
                  <th className="px-3 py-2 text-left font-medium text-zinc-400 text-xs">Maps to</th>
                </tr>
              </thead>
              <tbody>
                {headers.map((h) => (
                  <tr key={h} className="border-b border-zinc-800/40">
                    <td className="px-3 py-2 text-zinc-200">{h}</td>
                    <td className="px-3 py-2">
                      <Select
                        value={mapping[h] ?? ""}
                        onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                        className="h-8 text-xs"
                      >
                        <option value="">— Skip —</option>
                        {TARGET_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>If a contact already exists, match by</Label>
              <Select value={upsertBy} onChange={(e) => setUpsertBy(e.target.value as typeof upsertBy)}>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="none">Always create new</option>
              </Select>
            </div>
            <div>
              <Label>Add tag to all imported contacts</Label>
              <input
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="(optional)"
                className="h-9 w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500/50 focus:outline-none"
              />
            </div>
          </div>
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-900/30 px-3 py-2.5">
            <input
              type="checkbox"
              checked={returning}
              onChange={(e) => setReturning(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-indigo-500"
            />
            <span className="text-sm text-zinc-200">
              Everyone in this file has been in before
              <span className="block text-xs text-zinc-500">
                For your Boulevard client list. They can book any treatment online, and the front desk won&rsquo;t mark them as new. Untick for a list of leads who haven&rsquo;t visited yet.
              </span>
            </span>
          </label>
        </div>
      )}
      <ModalFooter>
        <Button variant="ghost" onClick={() => { onClose(); reset(); }}>
          {stage === "progress" && progress?.status !== "completed" && progress?.status !== "failed"
            ? "Close (import keeps running)"
            : "Close"}
        </Button>
        {stage === "map" && (
          <Button onClick={submit} loading={importing}>
            Import {rows.length.toLocaleString()} contact{rows.length === 1 ? "" : "s"}
          </Button>
        )}
        {stage === "progress" && (progress?.status === "completed" || progress?.status === "failed") && (
          <Button onClick={() => { onClose(); reset(); }}>Done</Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

/** Compact tile for the import progress view. */
function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "emerald" | "indigo" | "zinc";
}) {
  const ring =
    tone === "emerald" ? "border-emerald-500/30 bg-emerald-500/5"
    : tone === "indigo" ? "border-indigo-500/30 bg-indigo-500/5"
    : "border-zinc-800 bg-zinc-900/30";
  return (
    <div className={`rounded-md border ${ring} px-2.5 py-2`}>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-zinc-100 tabular-nums">{value}</p>
    </div>
  );
}
